import { useRef, useState } from 'react';
import { Avatar } from './Avatar.js';
import { ApiError, api } from './api.js';
import { Button, ErrorNote } from './ui.js';

/**
 * Envio da própria foto de perfil.
 *
 * Serve aos dois apps: é a mesma rota e a mesma regra dos dois lados. O
 * `capture` fica de fora de propósito — quem já tem uma foto boa na
 * galeria não deveria ser obrigado a tirar outra na hora.
 */
export function PhotoPicker({ name, hint }: { name: string; hint: string }) {
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** muda a cada envio para o Avatar rebuscar em vez de servir o cache */
  const [version, setVersion] = useState(0);

  async function send(file: File) {
    setBusy(true);
    setError(null);
    try {
      const form = new FormData();
      form.append('file', file);
      await api('/api/auth/me/photo', { formData: form });
      setVersion((n) => n + 1);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'não foi possível enviar a foto');
    } finally {
      setBusy(false);
      if (input.current) input.current.value = '';
    }
  }

  return (
    <div className="flex items-center gap-4">
      <Avatar name={name} src={`/api/auth/me/photo?v=${version}`} size="lg" />

      <div className="min-w-0 flex-1">
        <p className="font-semibold">Sua foto</p>
        <p className="mt-0.5 text-sm text-muted">{hint}</p>

        <input
          ref={input}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void send(file);
          }}
        />
        <Button
          variant="outline"
          className="mt-3"
          loading={busy}
          onClick={() => input.current?.click()}
        >
          Escolher foto
        </Button>

        {error && (
          <div className="mt-3">
            <ErrorNote>{error}</ErrorNote>
          </div>
        )}
      </div>
    </div>
  );
}
